import React, { Fragment } from "react";
import { styled } from "@mui/material/styles";
import { Button, IconButton, Typography } from "@mui/material";
import { Add, Remove } from "@mui/icons-material";

import ImageCarousel from "../ImageCarousel";
import Navbar from "@common/Navbar";
import AnimateWrapper from "@common/animations/AnimateWrapper";
import OutlinedButton from "@common/OutlinedButton";

import { isDark } from "@common/utils/colors";

const PREFIX = "SelectQuantity";

const classes = {
  imageDiv: `${PREFIX}-imageDiv`,
  quantityDiv: `${PREFIX}-quantityDiv`,
  instruction: `${PREFIX}-instruction`,
  selectQuantity: `${PREFIX}-selectQuantity`,
  iconButton: `${PREFIX}-iconButton`,
  quantity: `${PREFIX}-quantity`,
  actionButtons: `${PREFIX}-actionButtons`,
  checkoutButton: `${PREFIX}-checkoutButton`,
};

const StyledAnimateWrapper = styled(AnimateWrapper)(({ theme }) => ({
  display: "grid",
  placeItems: "center",
  gridTemplateColumns: "1fr 1fr",
  margin: "auto",
  maxWidth: 1280,
  padding: theme.spacing(5, 0),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    alignContent: "start",
    padding: 0,
    margin: "0 0 auto",
  },

  [`& .${classes.imageDiv}`]: {
    width: 550,
    height: 550,
    alignSelf: "flex-start",
    [theme.breakpoints.down("md")]: {
      width: "100%",
      height: "auto",
      marginTop: theme.spacing(3),
    },
  },

  [`& .${classes.quantityDiv}`]: {
    display: "flex",
    flexDirection: "column",
    width: "80%",
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      padding: theme.spacing(3, 0),
    },
  },

  [`& .${classes.instruction}`]: {
    fontWeight: 600,
    color: theme.palette.primary.contrastText,
  },

  [`& .${classes.selectQuantity}`]: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    maxWidth: "400px",
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
      marginTop: theme.spacing(3),
      width: "184px",
    },
  },

  [`& .${classes.iconButton}`]: {
    border: theme.bordered ? `2px solid ${theme.palette.secondary.main}` : "none",
    boxShadow: theme.bordered ? "none" : theme.shadows[2],
    backgroundColor: "#FFF",
    borderRadius: 4,
    color: isDark(theme.palette.secondary.main)
      ? theme.palette.secondary.main
      : isDark(theme.palette.secondary.contrastText)
      ? theme.palette.secondary.contrastText
      : theme.palette.secondary.main,
    [theme.breakpoints.down("md")]: {
      width: "40px",
      height: "40px",
    },
    "&:hover": {
      backgroundColor: "#e1e1e1",
      boxShadow: theme.bordered ? "none" : theme.shadows[3],
    },
    "&:disabled": {
      backgroundColor: "#ededed",
    },
  },

  [`& .${classes.quantity}`]: {
    fontWeight: 700,
    color: theme.palette.primary.contrastText,
  },

  [`& .${classes.actionButtons}`]: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "400px",
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(3),
    gap: theme.spacing(1),
  },

  [`& .${classes.checkoutButton}`]: {
    fontWeight: 600,
  },
}));

const SelectQuantity = ({
  pageNum,
  setPageNum,
  product,
  quantity,
  setQuantity,
  addToCarte,
  proceedToCheckout,
  selectedVariant,
}) => {
  const onBackRoutes = () => {
    if (product) {
      if (product.product_questions.length > 0) {
        return setPageNum(pageNum - 1);
      } else if (product.use_variants) {
        return setPageNum("SelectVariant");
      } else if (!product.use_variants) {
        return setPageNum("ProductDescription");
      }
    }
  };

  const reachedMaxInventory = () => {
    const _product = selectedVariant || product;
    if (_product && _product.inventory > 0) {
      return quantity >= _product.inventory;
    }
    return false;
  };

  return (
    <Fragment>
      <Navbar onBack={() => onBackRoutes()} progressBarOffSet />
      <StyledAnimateWrapper key="SelectQuantity">
        <div className={classes.imageDiv}>
          <ImageCarousel
            images={
              selectedVariant
                ? new Array({
                    image: selectedVariant.product_image,
                    image_caption: selectedVariant.product_image_caption,
                  })
                : product && product.images
            }
          />
        </div>
        <div className={classes.quantityDiv}>
          <Typography variant="subtitle1" className={classes.instruction}>
            How many would you like?
          </Typography>
          <div className={classes.selectQuantity}>
            <IconButton
              className={classes.iconButton}
              onClick={() => quantity > 1 && setQuantity(quantity - 1)}
              disabled={quantity <= 1}
              size="large"
            >
              <Remove />
            </IconButton>
            <Typography variant="h5" className={classes.quantity}>
              {quantity}
            </Typography>
            <IconButton
              className={classes.iconButton}
              onClick={() => setQuantity(quantity + 1)}
              disabled={reachedMaxInventory()}
              size="large"
            >
              <Add />
            </IconButton>
          </div>
          <div className={classes.actionButtons}>
            <Button
              variant="contained"
              className={classes.checkoutButton}
              color="secondary"
              onClick={() => proceedToCheckout()}
            >
              Checkout
            </Button>
            <OutlinedButton
              variant="outlined"
              sx={{
                color: (theme) => theme.palette.accent.main,
              }}
              onClick={() => addToCarte()}
            >
              Add to cart
            </OutlinedButton>
          </div>
        </div>
      </StyledAnimateWrapper>
    </Fragment>
  );
};

export default SelectQuantity;
